import { IonCheckbox, IonContent, IonItem, IonLabel, IonList, IonModal } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

interface QuestionData {
    sentence: string;
    correctOption: string[]; // Update to handle multiple correct nouns
}

const NamingWords: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]); // Store multiple selected nouns
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const [words, setWords] = useState<string[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);


    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'NamingWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/NamingWords.json');
                data = module.default;
            }


            const shuffledData = shuffleArray(data);
            setQuestions(shuffledData);
            const myQuestion = (shuffledData[currentIndex]?.sentence.split("."));
            setWords(myQuestion[0].split(" ") || []);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };


    useEffect(() => {
        fetchQuestions();
        document.title = "Naming Words Worksheets for UKG | Learn Nouns with Fun Activities"; // Set page title
    }, []);

    const handleOptionClick = (option: string) => {
        // Toggle the option in the selectedOptions array
        const updatedSelections = selectedOptions.includes(option)
            ? selectedOptions.filter((item) => item !== option) // Remove if already selected
            : [...selectedOptions, option]; // Add if not selected

        console.log(updatedSelections)

        setSelectedOptions(updatedSelections);

        const correctAnswers = questions[currentIndex]?.correctOption || [];

        // Check if all correct answers are selected
        const isCorrect =
            correctAnswers.every((noun) => updatedSelections.includes(noun)) &&
            updatedSelections.length === correctAnswers.length;

        if (isCorrect) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    const myQuestion = (questions[nextIndex]?.sentence.split("."));
                    setWords(myQuestion[0].split(" ") || []);
                    setSelectedOptions([]); // Reset selections
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setError(false);
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setError(false); // Reset error as user continues selecting
        }
    };




    return (
        <>
            <Helmet>
                <meta name="description" content='Help your UKG child learn about naming words (nouns) through fun and interactive activities at Mama’s Guidance, designed to build a strong English grammar foundation.' />
            </Helmet>
            <div className="contentPart showCenter commonNoun additionWord">
                <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                <IonModal
                    ref={modal}
                    trigger="open-modal"
                    initialBreakpoint={1}
                    breakpoints={[0, 0.5, 0.75, 1]}
                    handleBehavior="cycle"
                    backdropDismiss={false}  // ✅ Prevents click-to-close
                    className="full-page-modal"
                >
                    <IonContent className="ion-padding">
                        <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                        <h1>Naming Words</h1>

                        <p>
                            Naming words, also called <strong>nouns</strong>, are the first building blocks of grammar. At the UKG level, children are introduced to the concept of words that name <strong>people, places, animals, and things</strong>. These worksheets are designed to help children easily identify and use naming words in daily communication.
                        </p>

                        <h2>🔤 What Are Naming Words?</h2>
                        <p>
                            Naming words are words that tell us the <strong>names of people</strong> (like <em>mother, teacher</em>), <strong>places</strong> (like <em>school, park</em>), <strong>animals</strong> (like <em>dog, cat</em>), and <strong>things</strong> (like <em>ball, book</em>).
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Recognize and identify naming words (nouns)</li>
                            <li>Categorize nouns into people, places, animals, and things</li>
                            <li>Use naming words in simple sentences</li>
                            <li>Improve vocabulary and observation skills</li>
                        </ul>

                        <h2>🖍️ Activities in the Worksheets</h2>
                        <ul>
                            <li>Circle the naming words in a sentence</li>
                            <li>Match pictures with correct naming words</li>
                            <li>Sort nouns into groups (people, places, things, animals)</li>
                            <li>Fill in the blanks using correct nouns</li>
                            <li>Color the naming words from a word cloud</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Play “name it” games: Ask “What is this?” and let them say the name</li>
                            <li>Label everyday objects around the house</li>
                            <li>Use flashcards or drawings to reinforce naming word categories</li>
                            <li>Encourage children to speak in sentences using naming words</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/english-language/articles"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Articles</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/english-language/one-and-many"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>One and Many</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/english-language/rhyming-words"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Rhyming Words</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Point to objects around your home and ask your child to name them. Make it a fun challenge: “Let’s name 5 things in the kitchen!”
                        </p>

                        <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                    </IonContent>
                </IonModal>
                <article className="visually-hidden">
                    <h1>Naming Words</h1>

                    <p>
                        Naming words, also called <strong>nouns</strong>, are the first building blocks of grammar. At the UKG level, children are introduced to the concept of words that name <strong>people, places, animals, and things</strong>. These worksheets are designed to help children easily identify and use naming words in daily communication.
                    </p>

                    <h2>🔤 What Are Naming Words?</h2>
                    <p>
                        Naming words are words that tell us the <strong>names of people</strong> (like <em>mother, teacher</em>), <strong>places</strong> (like <em>school, park</em>), <strong>animals</strong> (like <em>dog, cat</em>), and <strong>things</strong> (like <em>ball, book</em>).
                    </p>

                    <h2>🎯 Learning Objectives</h2>
                    <ul>
                        <li>Recognize and identify naming words (nouns)</li>
                        <li>Categorize nouns into people, places, animals, and things</li>
                        <li>Use naming words in simple sentences</li>
                        <li>Improve vocabulary and observation skills</li>
                    </ul>

                    <h2>🖍️ Activities in the Worksheets</h2>
                    <ul>
                        <li>Circle the naming words in a sentence</li>
                        <li>Match pictures with correct naming words</li>
                        <li>Sort nouns into groups (people, places, things, animals)</li>
                        <li>Fill in the blanks using correct nouns</li>
                        <li>Color the naming words from a word cloud</li>
                    </ul>

                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                    <ul>
                        <li>Play “name it” games: Ask “What is this?” and let them say the name</li>
                        <li>Label everyday objects around the house</li>
                        <li>Use flashcards or drawings to reinforce naming word categories</li>
                        <li>Encourage children to speak in sentences using naming words</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/ukg/english-language/articles"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Articles</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/english-language/one-and-many"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>One and Many</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/english-language/rhyming-words"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Rhyming Words</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🌟 Mama’s Guidance Tip</h2>
                    <p>
                        Point to objects around your home and ask your child to name them. Make it a fun challenge: “Let’s name 5 things in the kitchen!”
                    </p>
                </article>
                {showImage === false ? (
                    <>
                        <h2 className="mt-5 mb-2" style={{ fontWeight: "normal" }}>
                            Q. Choose all the naming words below -
                        </h2>
                        <h2 className="mt-2 mb-2">{questions[currentIndex]?.sentence}</h2>
                        <div className="mt-5 mb-5 grid-container">
                            <div className="grid-box optionDiv">
                                <IonList className="grid-item-container">
                                    {words.map((option, index) => (
                                        <IonItem style={{ '--padding-start': "5px" }}
                                            className={`grid-item ${selectedOptions.includes(option)
                                                ? questions[currentIndex]?.correctOption.includes(option)
                                                    ? "correct"
                                                    : "errorOptions"
                                                : ""
                                                }`}
                                            key={index}
                                        >
                                            <IonCheckbox
                                                slot="start"
                                                checked={selectedOptions?.includes(option)}
                                                onIonChange={() => handleOptionClick(option)}
                                            />
                                            <IonLabel>{option}</IonLabel>
                                        </IonItem>
                                    ))}
                                </IonList>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="contentPart showMiddle">
                            <img
                                style={{ height: "150px" }}
                                className="img-fluid"
                                src={process.env.REACT_APP_WELDONE_LINK}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default NamingWords;
